import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useCallback} from 'react';

import {EventHandler, Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SimpleDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {useClipboard} from '../../../../../../helpers/hooks/clipboardHookHelpers';
import {buildTopicPublicLink} from '../../../../../../helpers/models/topicHelpers';
import {ActivityTopicTypesEnum} from '../../../../../../models/api/activityModel';
import {TopicModel} from '../../../../../../models/api/topicModel';
import {DropdownSeparator} from '../../../../../common/dropdowns/dropdownSeparator';

/*
 * Props.
 */

interface TopicDropdownProps extends DropdownProps {
  topic: TopicModel;
  openTopicLink: Handler;
  removeTopic?: Handler;
  searchByTopic: Handler;
  onEditTopicSelect: EventHandler;
  host: string;
}

/*
 * Component.
 */

export const TopicDropdown: FC<TopicDropdownProps> = (props) => {
  const {openTopicLink, host, topic, searchByTopic, removeTopic, onEditTopicSelect} = props;
  const {copy} = useClipboard();

  const onCopyPublicLinkClick = useCallback(() => {
    copy(buildTopicPublicLink(host, topic));
  }, [topic, copy, host]);

  return (
    <SimpleDropdown {...props} maxWidth={300}>
      <SimpleDropdownItem onSelect={openTopicLink}>
        <Trans id="topic-dropdown-open">Open &quot;{topic.name}&quot;</Trans>
      </SimpleDropdownItem>
      {maybeRenderEditTopic(topic, onEditTopicSelect)}
      <SimpleDropdownItem onSelect={removeTopic} isDisabled={isUndefined(removeTopic)}>
        <Trans id="topic-dropdown-remove">Remove link</Trans>
      </SimpleDropdownItem>
      <DropdownSeparator />
      <SimpleDropdownItem onSelect={searchByTopic}>
        <Trans id="topic-dropdown-related">View conversations</Trans>
      </SimpleDropdownItem>
      <SimpleDropdownItem onSelect={onCopyPublicLinkClick}>
        <Trans id="topic-dropdown-related-link">Copy link to conversations</Trans>
      </SimpleDropdownItem>
    </SimpleDropdown>
  );
};

function maybeRenderEditTopic(topic: TopicModel, onEditTopicSelect: EventHandler) {
  // Only topics of type web should be editable via the UI.
  if (topic.type !== ActivityTopicTypesEnum.WEB) {
    return null;
  }

  return (
    <SimpleDropdownItem onSelect={onEditTopicSelect}>
      <Trans id="topic-dropdown-edit">Edit</Trans>
    </SimpleDropdownItem>
  );
}
